/**
 * Componente DashboardHeader
 * 
 * Header do dashboard com título, descrição, botão de refresh
 * e estrutura preparada para filtros de período (futuro).
 * 
 * @module components/Dashboard/DashboardHeader/DashboardHeader
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaSync, FaSyncAlt } from 'react-icons/fa';
import { useDashboardData } from '../../../hooks/useDashboardData';
import { PeriodFilter, KPISelector } from '../DashboardFilters';
import { ExportButton } from '../DashboardExport';
import { RealtimeToggle } from '../RealtimeIndicator';

/**
 * Componente de Header do Dashboard
 * 
 * Renderiza o cabeçalho do dashboard com:
 * - Título e descrição
 * - Botão de refresh manual
 * - Estrutura preparada para filtros de período (futuro)
 * 
 * @param {object} props - Propriedades do componente
 * @param {string} [props.title] - Título personalizado (padrão: "Dashboard Gestão")
 * @param {string} [props.subtitle] - Subtítulo personalizado
 * @param {boolean} [props.showRefresh=true] - Se deve mostrar botão de refresh
 * @param {function} [props.onRefresh] - Callback quando refresh é acionado
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de header
 * 
 * @example
 * ```jsx
 * <DashboardHeader
 *     title="Dashboard Personalizado"
 *     subtitle="Métricas da organização"
 *     showRefresh={true}
 * />
 * ```
 */
const DashboardHeader = ({
    title = null,
    subtitle = null,
    showRefresh = true,
    onRefresh = null,
    className = '',
    // Novas props para filtros e personalização
    period = null,
    onPeriodChange = null,
    visibleKPIs = null,
    onKPIToggle = null,
    onResetPreferences = null,
    showFilters = true,
    // Props para exportação
    dashboardData = null,
    showExport = true,
    exportOptions = {},
    // Props para tempo real
    realtimeConfig = null,
    onRealtimeToggle = null,
    showRealtime = true,
}) => {
    const { refetchAll, isLoading } = useDashboardData();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const defaultTitle = 'Dashboard Gestão';
    const defaultSubtitle = 'Visão geral e métricas operacionais da sua organização';

    const displayTitle = title || defaultTitle;
    const displaySubtitle = subtitle || defaultSubtitle;

    /**
     * Handler para o botão de refresh
     * Executa refetch de todos os dados do dashboard
     * Memoizado com useCallback para evitar re-renderizações desnecessárias
     */
    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            // Se callback personalizado fornecido, usa ele
            if (onRefresh) {
                await onRefresh();
            } else {
                // Caso contrário, usa refetchAll do hook
                await refetchAll();
            }
        } catch (error) {
            console.error('Erro ao atualizar dashboard:', error);
        } finally {
            // Pequeno delay para feedback visual
            setTimeout(() => {
                setIsRefreshing(false);
            }, 500);
        }
    }, [onRefresh, refetchAll]);

    // Determina se está em estado de loading (qualquer hook carregando ou refresh manual)
    const isAnyLoading = isLoading || isRefreshing;

    return (
        <header 
            className={`mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${className}`} 
            data-testid="dashboard-header"
            role="banner"
        >
            {/* Título e Descrição */}
            <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 flex items-center">
                    <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                        {displayTitle}
                    </span>
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 font-medium" id="dashboard-subtitle">
                    {displaySubtitle}
                </p>
            </div>

            {/* Área de Ações (Refresh e Filtros) */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end sm:justify-start">
                {/* Filtros */}
                {showFilters && (
                    <>
                        {/* Filtro de Período */}
                        {period !== null && onPeriodChange && (
                            <PeriodFilter
                                period={period}
                                onPeriodChange={onPeriodChange}
                            />
                        )}
                        
                        {/* Seletor de KPIs */}
                        {visibleKPIs !== null && onKPIToggle && onResetPreferences && (
                            <KPISelector
                                visibleKPIs={visibleKPIs}
                                onKPIToggle={onKPIToggle}
                                onReset={onResetPreferences}
                            />
                        )}
                    </>
                )}
                
                {/* Tempo Real */}
                {showRealtime && realtimeConfig && onRealtimeToggle && (
                    <RealtimeToggle
                        isEnabled={realtimeConfig.isRealtimeEnabled}
                        onToggle={onRealtimeToggle}
                        isLoading={false}
                    />
                )}
                
                {/* Botão de Exportação */}
                {showExport && dashboardData && (
                    <ExportButton
                        dashboardData={dashboardData}
                        options={exportOptions}
                    />
                )}
                
                {/* Botão de Refresh */}
                {showRefresh && (
                    <button
                        onClick={handleRefresh}
                        onKeyDown={(e) => {
                            // Suporte para Enter e Space
                            if ((e.key === 'Enter' || e.key === ' ') && !isAnyLoading) {
                                e.preventDefault();
                                handleRefresh();
                            }
                        }}
                        disabled={isAnyLoading}
                        className="
                            group
                            flex items-center gap-1.5 sm:gap-2 
                            px-3 sm:px-4 py-2 sm:py-2.5 
                            text-xs sm:text-sm font-semibold
                            text-gray-700 
                            bg-gradient-to-r from-white to-gray-50
                            border border-gray-200 
                            rounded-xl 
                            shadow-sm
                            hover:from-gray-50 hover:to-gray-100
                            hover:border-emerald-300
                            hover:shadow-md
                            hover:scale-105
                            active:scale-100
                            active:from-gray-100 active:to-gray-200
                            transition-all duration-300 ease-in-out
                            focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                        "
                        aria-label={isAnyLoading ? 'Atualizando dados do dashboard' : 'Atualizar dados do dashboard'}
                        aria-busy={isAnyLoading}
                        title="Atualizar dados do dashboard"
                        type="button"
                    >
                        <FaSyncAlt 
                            className={`text-gray-500 group-hover:text-emerald-600 transition-colors duration-200 ${
                                isAnyLoading ? 'animate-spin' : ''
                            }`}
                            size={16}
                            aria-hidden="true"
                        />
                        <span className="group-hover:text-emerald-700 transition-colors duration-200 hidden sm:inline">
                            {isAnyLoading ? 'Atualizando...' : 'Atualizar'}
                        </span>
                    </button>
                )}

            </div>
        </header>
    );
};

DashboardHeader.propTypes = {
    /** Título personalizado */
    title: PropTypes.string,
    
    /** Subtítulo personalizado */
    subtitle: PropTypes.string,
    
    /** Se deve mostrar botão de refresh */
    showRefresh: PropTypes.bool,
    
    /** Callback quando refresh é acionado (opcional, usa refetchAll por padrão) */
    onRefresh: PropTypes.func,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
    
    /** Período selecionado (para filtro) */
    period: PropTypes.string,
    
    /** Callback quando período muda */
    onPeriodChange: PropTypes.func,
    
    /** Array de chaves de KPIs visíveis */
    visibleKPIs: PropTypes.arrayOf(PropTypes.string),
    
    /** Callback quando um KPI é alternado */
    onKPIToggle: PropTypes.func,
    
    /** Callback para resetar preferências */
    onResetPreferences: PropTypes.func,
    
    /** Se deve mostrar filtros */
    showFilters: PropTypes.bool,
    
    /** Dados do dashboard para exportação */
    dashboardData: PropTypes.shape({
        summary: PropTypes.object,
        taskDistribution: PropTypes.array,
        workload: PropTypes.array,
        equipmentStatus: PropTypes.array,
        pontoDesvios: PropTypes.array,
        estoqueAlertas: PropTypes.array,
    }),
    
    /** Se deve mostrar botão de exportação */
    showExport: PropTypes.bool,
    
    /** Opções de exportação */
    exportOptions: PropTypes.shape({
        period: PropTypes.string,
        visibleKPIs: PropTypes.arrayOf(PropTypes.string),
        organizationName: PropTypes.string,
    }),
    
    /** Configuração de tempo real */
    realtimeConfig: PropTypes.shape({
        isRealtimeEnabled: PropTypes.bool,
        isConnected: PropTypes.bool,
        lastUpdate: PropTypes.instanceOf(Date),
    }),
    
    /** Callback quando tempo real é alternado */
    onRealtimeToggle: PropTypes.func,
    
    /** Se deve mostrar controles de tempo real */
    showRealtime: PropTypes.bool,
};

// Default props removidos - usando default parameters na função

export default DashboardHeader;

